import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CryptoCard from './components/CryptoCard';
import MarketOverview from './components/MarketOverview';
import PriceChart from './components/PriceChart';
import WatchlistManager from './components/WatchlistManager';
import PriceAlerts from './components/PriceAlerts';
import TradingWidget from './components/TradingWidget';

const CryptoDashboard = () => {
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [refreshing, setRefreshing] = useState(false);

  // Mock cryptocurrency data
  const cryptoData = [
  {
    id: 'bitcoin',
    name: 'Bitcoin',
    symbol: 'BTC',
    price: 43250.50,
    change: 2.34,
    marketCap: 847500000000,
    volume: 28500000000,
    high24h: 44120.30,
    low24h: 42180.75,
    image: "https://images.unsplash.com/photo-1643616997533-b2765f43011d",
    imageAlt: 'Golden Bitcoin cryptocurrency coin on dark background with digital circuit patterns'
  },
  {
    id: 'ethereum',
    name: 'Ethereum',
    symbol: 'ETH',
    price: 2456.78,
    change: -1.23,
    marketCap: 295400000000,
    volume: 15200000000,
    high24h: 2510.45,
    low24h: 2398.20,
    image: "https://images.unsplash.com/photo-1644926054948-8c1155eeb0e1",
    imageAlt: 'Silver Ethereum cryptocurrency coin with distinctive diamond logo on technological background'
  },
  {
    id: 'aogx',
    name: 'AOGX Token',
    symbol: 'AOGX',
    price: 0.9234,
    change: 5.67,
    marketCap: 92340000,
    volume: 2100000,
    high24h: 0.9856,
    low24h: 0.8745,
    image: "https://images.unsplash.com/photo-1667134387582-f3673e7ecc90",
    imageAlt: 'Modern cryptocurrency token with blue and purple gradient design on futuristic digital background'
  },
  {
    id: 'cardano',
    name: 'Cardano',
    symbol: 'ADA',
    price: 0.4521,
    change: 3.45,
    marketCap: 15800000000,
    volume: 890000000,
    high24h: 0.4678,
    low24h: 0.4321,
    image: "https://images.unsplash.com/photo-1665236106755-5e7fbca083aa",
    imageAlt: 'Blue Cardano cryptocurrency coin with circular logo design on clean white background'
  },
  {
    id: 'solana',
    name: 'Solana',
    symbol: 'SOL',
    price: 98.34,
    change: -2.11,
    marketCap: 42100000000,
    volume: 1800000000,
    high24h: 102.45,
    low24h: 96.78,
    image: "https://images.unsplash.com/photo-1630569871950-fa11a57fd766",
    imageAlt: 'Purple and pink gradient Solana cryptocurrency coin with geometric logo on dark tech background'
  },
  {
    id: 'binancecoin',
    name: 'BNB',
    symbol: 'BNB',
    price: 312.45,
    change: 1.89,
    marketCap: 48200000000,
    volume: 1200000000,
    high24h: 318.90,
    low24h: 305.67,
    image: "https://images.unsplash.com/photo-1655871282339-46b57cb4b8f3",
    imageAlt: 'Yellow and black Binance coin cryptocurrency with diamond-shaped logo on golden background'
  },
  {
    id: 'ripple',
    name: 'XRP',
    symbol: 'XRP',
    price: 0.6234,
    change: 0.78,
    marketCap: 33800000000,
    volume: 1500000000,
    high24h: 0.6456,
    low24h: 0.6123,
    image: "https://images.unsplash.com/photo-1651055719729-a13c1035ea45",
    imageAlt: 'Silver XRP Ripple cryptocurrency coin with distinctive X logo on blue digital background'
  },
  {
    id: 'dogecoin',
    name: 'Dogecoin',
    symbol: 'DOGE',
    price: 0.0823,
    change: 4.56,
    marketCap: 11700000000,
    volume: 780000000,
    high24h: 0.0867,
    low24h: 0.0789,
    image: "https://images.unsplash.com/photo-1631865335944-80c85b287aa4",
    imageAlt: 'Golden Dogecoin cryptocurrency featuring the famous Shiba Inu dog meme on orange background'
  },
  {
    id: 'polygon',
    name: 'Polygon',
    symbol: 'MATIC',
    price: 0.8934,
    change: 2.67,
    marketCap: 8200000000,
    volume: 450000000,
    high24h: 0.9234,
    low24h: 0.8567,
    image: "https://images.unsplash.com/photo-1630569871950-fa11a57fd766",
    imageAlt: 'Purple Polygon MATIC cryptocurrency coin with geometric network design on tech background'
  },
  {
    id: 'chainlink',
    name: 'Chainlink',
    symbol: 'LINK',
    price: 14.56,
    change: -0.89,
    marketCap: 8100000000,
    volume: 380000000,
    high24h: 15.23,
    low24h: 14.12,
    image: "https://images.unsplash.com/photo-1728305932570-6bb09cf20337",
    imageAlt: 'Blue Chainlink cryptocurrency coin with interconnected chain logo on network background'
  }];


  // Stablecoins data
  const stablecoins = [
  {
    id: 'tether',
    name: 'Tether',
    symbol: 'USDT',
    price: 1.0001,
    change: 0.01,
    marketCap: 83200000000,
    volume: 45600000000,
    image: "https://images.unsplash.com/photo-1626163015484-81fc7e3b90d8",
    imageAlt: 'Green Tether USDT stablecoin with dollar symbol on stable financial background'
  },
  {
    id: 'usd-coin',
    name: 'USD Coin',
    symbol: 'USDC',
    price: 0.9998,
    change: -0.02,
    marketCap: 25100000000,
    volume: 8900000000,
    image: "https://images.unsplash.com/photo-1633181327750-c9775d6a7da8",
    imageAlt: 'Blue and white USD Coin stablecoin with circular design on clean background'
  },
  {
    id: 'dai',
    name: 'Dai',
    symbol: 'DAI',
    price: 1.0003,
    change: 0.03,
    marketCap: 5200000000,
    volume: 180000000,
    image: "https://images.unsplash.com/photo-1666092109883-4b1f221ffa58",
    imageAlt: 'Orange Dai stablecoin with geometric logo design on decentralized finance background'
  }];


  useEffect(() => {
    // Set AOGX as the default selected crypto
    const aogxToken = cryptoData?.find((crypto) => crypto?.symbol === 'AOGX');
    if (aogxToken) {
      setSelectedCrypto(aogxToken);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setRefreshing(false);
  };

  const handleAddToWatchlist = (token) => {
    if (!watchlist?.find((item) => item?.symbol === token?.symbol)) {
      setWatchlist([...watchlist, token]);
    }
  };

  const topCryptos = cryptoData?.slice(0, 10);
  const aogxToken = cryptoData?.find((crypto) => crypto?.symbol === 'AOGX');

  return (
    <>
      <Helmet>
        <title>Crypto Dashboard - REXFEL Investment Platform</title>
        <meta name="description" content="Real-time cryptocurrency tracking dashboard featuring top 10 tokens, stablecoins, and AOGX token performance with interactive charts and market analysis." />
        <meta name="keywords" content="cryptocurrency, crypto dashboard, AOGX token, bitcoin, ethereum, market data, trading, investment" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-primary/5 via-accent/5 to-background py-16">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-brand flex items-center justify-center">
                    <Icon name="TrendingUp" size={24} className="text-white" />
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold text-gradient-brand">
                    Crypto Dashboard
                  </h1>
                </div>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Real-time cryptocurrency market intelligence with advanced analytics, 
                  portfolio tracking, and institutional-grade trading insights.
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  onClick={() => setViewMode('grid')}
                  iconName="Grid3X3"
                  iconPosition="left">

                  Grid View
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  onClick={() => setViewMode('list')}
                  iconName="List"
                  iconPosition="left">

                  List View
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  loading={refreshing}
                  iconName="RefreshCw"
                  iconPosition="left">

                  Refresh Data
                </Button>
              </div>
            </div>
          </section>

          {/* Market Overview */}
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-6">
              <MarketOverview />
            </div>
          </section>

          {/* AOGX Featured Section */}
          <section className="py-12 bg-gradient-to-r from-primary/5 to-accent/5">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Featured: AOGX Token
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Our flagship digital asset with advanced tokenomics and strategic market positioning
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                  {aogxToken && <CryptoCard crypto={aogxToken} isAOGX={true} />}
                </div>
                <div className="lg:col-span-2">
                  {aogxToken && <PriceChart crypto={aogxToken} />}
                </div>
              </div>
            </div>
          </section>

          {/* Top 10 Cryptocurrencies */}
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-3xl font-bold text-foreground mb-2">
                    Top 10 Cryptocurrencies
                  </h2>
                  <p className="text-muted-foreground">
                    Market leaders by capitalization and trading volume
                  </p>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                  <span>Live prices updated every 30 seconds</span>
                </div>
              </div>

              <div className={`grid gap-6 ${
              viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`
              }>
                {topCryptos?.map((crypto) =>
                <div
                  key={crypto?.id}
                  onClick={() => setSelectedCrypto(crypto)}
                  className="cursor-pointer">

                    <CryptoCard
                    crypto={crypto}
                    isAOGX={crypto?.symbol === 'AOGX'} />

                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Stablecoins Section */}
          <section className="py-12 bg-muted/30">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-foreground mb-2">
                  Leading Stablecoins
                </h2>
                <p className="text-muted-foreground">
                  Price-stable cryptocurrencies pegged to fiat currencies
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {stablecoins?.map((stablecoin) =>
                <CryptoCard key={stablecoin?.id} crypto={stablecoin} />
                )}
              </div>
            </div>
          </section>

          {/* Interactive Tools */}
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Advanced Trading Tools
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Professional-grade tools for portfolio management, price alerts, and market analysis
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <WatchlistManager onAddToWatchlist={handleAddToWatchlist} />
                <PriceAlerts />
              </div>

              <TradingWidget />
            </div>
          </section>

          {/* Selected Crypto Chart */}
          {selectedCrypto &&
          <section className="py-12 bg-card/50">
              <div className="max-w-7xl mx-auto px-6">
                <div className="mb-6">
                  <Button
                  variant="ghost"
                  onClick={() => setSelectedCrypto(null)}
                  iconName="X"
                  iconPosition="left"
                  className="mb-4">

                    Close Chart
                  </Button>
                </div>
                <PriceChart crypto={selectedCrypto} />
              </div>
            </section>
          }

          {/* Market Intelligence Footer */}
          <section className="py-12 bg-gradient-to-r from-primary/10 to-accent/10">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  Market Intelligence & Analysis
                </h3>
                <p className="text-muted-foreground mb-8 max-w-3xl mx-auto">
                  Stay ahead with REXFEL's comprehensive market analysis, institutional insights, 
                  and strategic investment intelligence powered by real-time data feeds.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-card border border-border rounded-brand p-6">
                    <Icon name="BarChart3" size={32} className="text-primary mx-auto mb-4" />
                    <h4 className="font-semibold text-foreground mb-2">Real-Time Analytics</h4>
                    <p className="text-sm text-muted-foreground">
                      Live market data with institutional-grade analysis and predictive insights
                    </p>
                  </div>
                  
                  <div className="bg-card border border-border rounded-brand p-6">
                    <Icon name="Shield" size={32} className="text-success mx-auto mb-4" />
                    <h4 className="font-semibold text-foreground mb-2">Secure Infrastructure</h4>
                    <p className="text-sm text-muted-foreground">
                      Enterprise-level security with encrypted data transmission and secure APIs
                    </p>
                  </div>
                  
                  <div className="bg-card border border-border rounded-brand p-6">
                    <Icon name="Zap" size={32} className="text-accent mx-auto mb-4" />
                    <h4 className="font-semibold text-foreground mb-2">Lightning Fast</h4>
                    <p className="text-sm text-muted-foreground">
                      Sub-second data updates with optimized caching and failover systems
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Button variant="default" iconName="TrendingUp" iconPosition="left">
                    View Market Analysis
                  </Button>
                  <Button variant="outline" iconName="Download" iconPosition="left">
                    Export Data
                  </Button>
                  <Button variant="outline" iconName="Settings" iconPosition="left">
                    Customize Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center space-x-4 mb-4 md:mb-0">
                <div className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} REXFEL Technology and Innovation. All rights reserved.
                </div>
              </div>
              
              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} />
                  <span>Market data updated: {new Date()?.toLocaleTimeString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Globe" size={16} />
                  <span>Global markets 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>);

};

export default CryptoDashboard;